import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '../ui/Button';

interface ResourceItem {
  title: string;
  description: string;
  icon: string;
  action: string;
  link: string;
  type: 'document' | 'video' | 'community';
  badge?: string;
}

export const ResourcesPage: React.FC = () => {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState<string>('all');

  const filterButtons = [
    { id: 'all', name: 'All Resources', icon: 'fas fa-th' },
    { id: 'document', name: 'Documents', icon: 'fas fa-file-alt' },
    { id: 'video', name: 'Videos', icon: 'fas fa-video' },
    { id: 'community', name: 'Community', icon: 'fas fa-users' }
  ];

  const resources: ResourceItem[] = [
    {
      title: 'Whitepaper',
      description: 'Comprehensive overview of IDNGOLD technology, tokenomics, and roadmap',
      icon: 'fas fa-book',
      action: 'Read Whitepaper',
      link: '/whitepaper',
      type: 'document',
      badge: 'Technical'
    },
    {
      title: 'One-Pager',
      description: 'Quick summary of IDNGOLD project for investors and partners',
      icon: 'fas fa-file',
      action: 'Download PDF',
      link: '#',
      type: 'document',
      badge: 'Business'
    },
    {
      title: 'Technical Documentation',
      description: 'Developer documentation for integration and API usage',
      icon: 'fas fa-code',
      action: 'View Docs',
      link: '#',
      type: 'document',
      badge: 'Developer'
    },
    {
      title: 'Video Tutorials',
      description: 'Step-by-step guides for using IDNGOLD platform and features',
      icon: 'fas fa-play-circle',
      action: 'Watch Videos',
      link: '#',
      type: 'video',
      badge: 'Educational'
    },
    {
      title: 'FAQ',
      description: 'Frequently asked questions about IDNGOLD and gold-backed NFTs',
      icon: 'fas fa-question-circle',
      action: 'View FAQ',
      link: '/faq',
      type: 'document',
      badge: 'Support'
    }
  ];

  const communityResources = [
    {
      name: 'Telegram',
      description: 'Join our Telegram community for real-time discussions',
      icon: 'fab fa-telegram',
      link: '#',
      members: '10K+',
      color: 'from-blue-500/20 to-blue-500/5'
    },
    {
      name: 'Discord',
      description: 'Connect with our team and community on Discord',
      icon: 'fab fa-discord',
      link: '#',
      members: '5K+',
      color: 'from-purple-500/20 to-purple-500/5'
    },
    {
      name: 'Twitter',
      description: 'Follow us on Twitter for news and announcements',
      icon: 'fab fa-twitter',
      link: '#',
      members: '50K+',
      color: 'from-sky-500/20 to-sky-500/5'
    },
    {
      name: 'GitHub',
      description: 'View our open-source code and contribute',
      icon: 'fab fa-github',
      link: '#',
      members: '100+',
      color: 'from-gray-500/20 to-gray-500/5'
    }
  ];

  const filteredResources = activeFilter === 'all'
    ? resources
    : resources.filter(resource => resource.type === activeFilter);

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="floating-element"></div>
        <div className="floating-element"></div>
        <div className="floating-element"></div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="hero-background absolute inset-0">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 100 0 L 0 0 0 100' fill='none' stroke='rgba(212, 175, 55, 0.1)' stroke-width='1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)' /%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
          <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
            <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('pages.resources.title')}</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-8 leading-tight bg-gradient-to-r from-gold to-gold-light bg-clip-text text-transparent">
            {t('pages.resources.title')}
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            {t('pages.resources.intro')}
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="section relative">
        <div className="container">
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            {filterButtons.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`group relative px-6 py-3 rounded-full transition-all duration-300 ${
                  activeFilter === filter.id
                    ? 'bg-gold text-black font-bold'
                    : 'bg-gold/10 text-gold hover:bg-gold/20'
                }`}
              >
                <span className="relative z-10 flex items-center gap-2">
                  <i className={`${filter.icon} text-sm`}></i>
                  {filter.name}
                </span>
                {activeFilter === filter.id && (
                  <div className="absolute inset-0 bg-gradient-to-r from-gold to-gold-light rounded-full"></div>
                )}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {filteredResources.map((resource, index) => (
              <div key={index} className="modern-card group">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className={`${resource.icon} text-2xl text-gold`}></i>
                  </div>
                  {resource.badge && (
                    <div className="inline-block px-3 py-1 bg-gold/10 rounded-full">
                      <span className="text-gold text-xs font-semibold uppercase tracking-wider">{resource.badge}</span>
                    </div>
                  )}
                </div>

                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors">
                  {resource.title}
                </h3>

                <p className="text-gray-400 leading-relaxed mb-6">
                  {resource.description}
                </p>

                <Link
                  to={resource.link}
                  className="inline-flex items-center gap-3 text-gold hover:text-gold-light font-semibold transition-all group-hover:translate-x-2"
                >
                  {resource.action}
                  <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
                </Link>

                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-gold to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tutorials Section */}
      <section className="section bg-bg-secondary/50 relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">Tutorials</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">{t('pages.resources.tutorials.title')}</h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              {t('pages.resources.tutorials.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              { title: 'Getting Started', duration: '5 min', level: 'Beginner' },
              { title: 'Wallet Setup', duration: '8 min', level: 'Beginner' },
              { title: 'Buying IDNG', duration: '10 min', level: 'Intermediate' },
              { title: 'NFT Minting', duration: '12 min', level: 'Advanced' }
            ].map((tutorial, index) => (
              <div key={index} className="glass-card text-center group cursor-pointer">
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-red-500/20 to-red-500/5 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                    <i className="fas fa-play text-4xl text-red-400"></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-gold rounded-full flex items-center justify-center">
                    <span className="text-xs font-bold text-black">{index + 1}</span>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-gold transition-colors">
                  {tutorial.title}
                </h3>
                <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <i className="fas fa-clock"></i>
                    {tutorial.duration}
                  </span>
                  <span className="px-2 py-1 bg-gold/10 rounded text-gold text-xs">
                    {tutorial.level}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button variant="primary" size="lg" className="group relative overflow-hidden">
              <span className="relative z-10 flex items-center">
                <i className="fas fa-play mr-3 group-hover:rotate-12 transition-transform"></i>
                View All Tutorials
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-indonesia-red to-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </Button>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="section relative">
        <div className="container">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">Community</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">{t('pages.resources.community.title')}</h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              {t('pages.resources.community.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {communityResources.map((community, index) => (
              <div key={index} className="partner-card group">
                <div className="text-center">
                  <div className={`w-24 h-24 bg-gradient-to-br ${community.color} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform`}>
                    <i className={`${community.icon} text-5xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-gold transition-colors">
                    {community.name}
                  </h3>
                  <p className="text-gray-400 leading-relaxed mb-4">
                    {community.description}
                  </p>
                  <div className="flex items-center justify-center gap-2 mb-6">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-sm text-gray-500">{community.members} members</span>
                  </div>
                  <a
                    href={community.link}
                    className="inline-flex items-center gap-2 text-gold hover:text-gold-light font-semibold transition-all group-hover:translate-x-2"
                  >
                    Join Now
                    <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="section bg-bg-secondary/50 relative">
        <div className="container">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">Impact</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">By the Numbers</h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              Growing community and resources
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-black text-gold mb-2">50K+</div>
              <div className="text-gray-400">Community Members</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-black text-gold mb-2">100+</div>
              <div className="text-gray-400">Video Tutorials</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-black text-gold mb-2">24/7</div>
              <div className="text-gray-400">Community Support</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-black text-gold mb-2">10+</div>
              <div className="text-gray-400">Languages</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section pb-20">
        <div className="container">
          <div className="cta-section relative">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 relative z-10">Need More Help?</h2>
            <p className="text-xl text-gray-400 mb-12 max-w-3xl mx-auto leading-relaxed relative z-10">
              Our team is here to support your journey with IDNGold
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center relative z-10">
              <Link to="/faq">
                <Button variant="primary" size="lg" className="group relative overflow-hidden px-8 py-4 text-lg font-bold">
                  <span className="relative z-10">Browse FAQ</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-indonesia-red to-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </Button>
              </Link>
              <a
                href="mailto:support@idngold.com"
                className="group relative overflow-hidden px-8 py-4 text-lg font-bold bg-gradient-to-r from-gold/20 to-gold/10 text-gold rounded-full border border-gold/30 hover:from-gold/30 hover:to-gold/20 transition-all duration-300 text-center"
              >
                <span className="relative z-10 flex items-center">
                  <i className="fas fa-envelope mr-3"></i>
                  Contact Support
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};